import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import { fmtNum } from "@/data/gameData";
import { ArrowLeft, Flame, Loader2, CheckCircle2, Sparkles, Lightbulb, Share2 } from "lucide-react";

export default function Daily() {
  const { http } = useAuth();
  const navigate = useNavigate();
  const [ch, setCh] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answer, setAnswer] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    http.get("/daily").then(({ data }) => {
      setCh(data);
      if (data.done) setAnswer({ ...data.resposta, distribution: data.distribution });
    }).catch(() => {}).finally(() => setLoading(false));
  }, [http]);

  const pick = async (i) => {
    if (submitting || answer) return;
    setSubmitting(true);
    try {
      const { data } = await http.post("/daily/answer", { index: ch.index, choice: i });
      setAnswer(data);
    } catch (err) { console.error("daily answer falhou", err); toast.error("Não foi possível registrar sua escolha."); }
    finally { setSubmitting(false); }
  };

  const shareDaily = async () => {
    const text = `EU ESCOLHI: "${answer.escolha}"\nDesafio do dia — E SE FOSSE COMIGO?\nE você, o que faria?`;
    if (navigator.share) {
      try { await navigator.share({ title: "E Se Fosse Comigo?", text, url: window.location.origin }); return; }
      catch (err) { console.debug("share cancelado", err); }
    }
    try { await navigator.clipboard.writeText(`${text}\n${window.location.origin}`); toast.success("Decisão copiada!"); }
    catch (err) { console.error("clipboard falhou", err); toast.error("Não foi possível compartilhar."); }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-2xl mx-auto px-5 py-8">
        <button onClick={() => navigate("/")} className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Voltar
        </button>

        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F59E0B]/15 border border-[#F59E0B]/30 text-[#F59E0B] text-xs font-semibold uppercase tracking-widest mb-4">
          <Flame className="w-4 h-4" /> Desafio do dia
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#F43F5E]" /></div>
        ) : ch ? (
          <>
            <h1 className="font-display font-black text-3xl tracking-tighter">{ch.titulo}</h1>
            <div className="rounded-3xl p-6 bg-[#0F0F14] border border-white/10 mt-5">
              <p data-testid="daily-narrative" className="font-display text-xl leading-snug">{ch.narrativa}</p>
            </div>

            {!answer ? (
              <div className="space-y-3 mt-5">
                {ch.opcoes.map((o, i) => (
                  <button key={o.texto} data-testid={`daily-option-${i}`} onClick={() => pick(i)} disabled={submitting}
                    className="group w-full text-left rounded-2xl p-5 bg-[#0F0F14] border border-white/10 hover:border-[#F59E0B]/60 active:scale-[0.98] transition-all disabled:opacity-60">
                    <div className="flex items-start gap-3">
                      <span className="mt-0.5 w-6 h-6 shrink-0 grid place-items-center rounded-full bg-white/5 border border-white/10 text-xs font-bold text-slate-400 group-hover:bg-[#F59E0B] group-hover:text-black transition-colors">
                        {String.fromCharCode(65 + i)}
                      </span>
                      <span className="text-[15px] leading-snug">{o.texto}</span>
                    </div>
                  </button>
                ))}
                {submitting && <div className="flex justify-center py-4"><Loader2 className="w-6 h-6 animate-spin text-[#F59E0B]" /></div>}
              </div>
            ) : (
              <AnimatePresence>
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                  data-testid="daily-result" className="mt-5">
                  <div className="rounded-3xl p-6 bg-gradient-to-br from-[#F59E0B]/15 to-[#F43F5E]/10 border border-[#F59E0B]/25">
                    <div className="flex items-center gap-2 text-[#F59E0B] mb-3">
                      <CheckCircle2 className="w-5 h-5" />
                      <span className="text-sm font-semibold">Consequência</span>
                    </div>
                    {answer.escolha && <p className="text-sm text-slate-400 mb-2">Você escolheu: <span className="text-white">{answer.escolha}</span></p>}
                    <p className="font-display text-lg leading-snug">{answer.consequencia}</p>
                    <div className="flex items-center gap-2 mt-4 text-[#F43F5E]">
                      <Sparkles className="w-4 h-4" />
                      <span className="font-display font-black text-xl">+{fmtNum(answer.pontos)} pontos</span>
                    </div>
                  </div>

                  {answer.explicacao && (
                    <div className="rounded-2xl p-4 bg-white/5 border border-white/10 flex items-start gap-3 mt-3">
                      <Lightbulb className="w-5 h-5 text-[#06B6D4] shrink-0 mt-0.5" />
                      <p className="text-sm text-slate-300">{answer.explicacao}</p>
                    </div>
                  )}

                  <div data-testid="daily-comparison" className="rounded-2xl p-5 bg-[#0F0F14] border border-white/10 mt-3">
                    <p className="text-xs uppercase tracking-widest text-slate-500 mb-3">O que os outros escolheram?</p>
                    {answer.distribution?.enough ? (
                      <div className="space-y-2.5">
                        {answer.distribution.dist.map((d, i) => (
                          <div key={d.choice}>
                            <div className="flex justify-between text-sm mb-1">
                              <span className={i === answer.choice ? "text-white font-semibold" : "text-slate-400"}>
                                {String.fromCharCode(65 + i)}{i === answer.choice ? " (você)" : ""}
                              </span>
                              <span className="text-slate-300">{d.pct}%</span>
                            </div>
                            <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                              <motion.div className="h-full rounded-full" initial={{ width: 0 }} animate={{ width: `${d.pct}%` }}
                                transition={{ duration: 0.6 }} style={{ background: i === answer.choice ? "#F43F5E" : "#475569" }} />
                            </div>
                          </div>
                        ))}
                        <p className="text-[11px] text-slate-500 mt-1">Baseado em {fmtNum(answer.distribution.total)} respostas reais de jogadores hoje.</p>
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400">Poucos jogadores decidiram hoje ainda. Volte mais tarde para ver como você se compara! 📊</p>
                    )}
                  </div>

                  <button data-testid="daily-share" onClick={shareDaily}
                    className="mt-3 w-full flex items-center justify-center gap-2 rounded-2xl py-3.5 bg-white/5 border border-white/10 hover:bg-white/10 active:scale-95 transition-all font-semibold">
                    <Share2 className="w-4 h-4" /> Compartilhar minha decisão
                  </button>

                  <p className="text-center text-xs text-slate-400 mt-4">Volte amanhã para um novo desafio.</p>
                  <button onClick={() => navigate("/historias")}
                    className="mt-4 w-full rounded-2xl py-3.5 bg-[#F43F5E] hover:bg-[#E11D48] active:scale-95 transition-all font-semibold text-white">
                    Jogar uma história completa
                  </button>
                </motion.div>
              </AnimatePresence>
            )}
            <p className="text-[11px] text-slate-600 mt-6 text-center">Ficção para entretenimento. Não é aconselhamento financeiro, médico ou jurídico.</p>
          </>
        ) : null}
      </div>
    </div>
  );
}
